import { memo, useCallback, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { formatMinutesToTime } from '@/helpers';
import { cn } from '@/lib/utils';
import { OpsiumUserShortResponse } from '@/modules/common';

import { PLANNER_TIMEOFF_TOOLTIP_HALF_WIDTH_PX } from '../../constants';
import { TimeOffSegment } from '../../helpers/time-off-day-stacking';
import usePlannerStore from '../../store';
import { TimeOffRequestStatusDTO } from '../../types/dto/response/planner-timeoff-response';
import { ScrollableText } from '../assignment/text/scrollable-text';
import { usePlannerTooltip } from '../tooltip/planner-tooltip-provider';
import { useStickyTimeOffLabelActive } from './text/sticky-time-off-labels-context';
import { TimeOffTooltipContent } from './time-off-tooltip-content';

interface TimeOffBlockProps {
  /** Pre-resolved segment (uniform whole-block or a single non-uniform day). */
  segment: TimeOffSegment;
  user?: OpsiumUserShortResponse;
  dates: dayjs.Dayjs[];
  cellWidth: number;
}

const TIME_OFF_TITLE_ANIMATION_DELAY_MS = 300;

export const TimeOffBlock = memo(({ segment, user, dates, cellWidth }: TimeOffBlockProps) => {
  const { t } = useTranslation('authenticated.planner');
  const { interactiveTooltip } = usePlannerTooltip();
  const { show: showTooltip, hide: hideTooltipWithDelay } = interactiveTooltip;
  const tooltipOffsetRef = useRef<number>(0);

  const { timeOff, startDate, endDate, rect, dayMinutes, portion, dayPartNumber } = segment;

  const { hoveredTimeOffId, setHoveredTimeOffId, clearHoveredTimeOffIdIf } = usePlannerStore(
    useShallow(state => ({
      hoveredTimeOffId: state.hoveredTimeOffId,
      setHoveredTimeOffId: state.setHoveredTimeOffId,
      clearHoveredTimeOffIdIf: state.clearHoveredTimeOffIdIf,
    }))
  );

  const hasStickyLabel = useStickyTimeOffLabelActive(timeOff.id, startDate);
  const handleTimeOffHide = useCallback(
    () => clearHoveredTimeOffIdIf(timeOff.id),
    [clearHoveredTimeOffIdIf, timeOff.id]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setHoveredTimeOffId(timeOff.id);

      const blockRect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX;
      tooltipOffsetRef.current = mouseX - blockRect.left - PLANNER_TIMEOFF_TOOLTIP_HALF_WIDTH_PX;

      showTooltip(
        <TimeOffTooltipContent
          timeOff={timeOff}
          user={user}
          dayMinutes={dayMinutes}
          portion={portion}
          dayPartNumber={dayPartNumber}
        />,
        e.currentTarget,
        tooltipOffsetRef.current,
        handleTimeOffHide,
        // hideKey = time-off id: moving between segments of the same time-off keeps
        // the lift; a different time-off still clears it.
        timeOff.id
      );
    },
    [
      showTooltip,
      timeOff,
      user,
      dayMinutes,
      portion,
      dayPartNumber,
      setHoveredTimeOffId,
      handleTimeOffHide,
    ]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredTimeOffId(null);
    hideTooltipWithDelay();
  }, [hideTooltipWithDelay, setHoveredTimeOffId]);

  const label = useMemo(
    () =>
      t('timeOff.blockLabel', {
        title: timeOff.title,
        time: formatMinutesToTime(dayMinutes),
      }),
    [timeOff.title, dayMinutes, t]
  );

  const baseDate = dates[0];

  const { startOffsetRem, widthRem } = useMemo(() => {
    if (!baseDate) return { startOffsetRem: 0, widthRem: 0 };
    const start = startDate.startOf('day').diff(baseDate.startOf('day'), 'day') * cellWidth;
    const width = (endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1) * cellWidth;
    return { startOffsetRem: start, widthRem: width };
  }, [baseDate, startDate, endDate, cellWidth]);

  if (!baseDate || widthRem <= 0) return null;

  const isPending = timeOff.status === TimeOffRequestStatusDTO.PENDING;
  // A segment is single-day when its span covers one calendar day.
  const isSingleDay = endDate.startOf('day').diff(startDate.startOf('day'), 'day') === 0;
  const isHovered = hoveredTimeOffId === timeOff.id;

  return (
    <div
      className={cn(
        'pointer-events-auto absolute flex items-center justify-center overflow-hidden rounded-md border transition-transform duration-150',
        // Approved = solid cyan, pending = diagonal cyan stripes (shared classes).
        // Both PENDING (z-0) and APPROVED (z-[1]) sit BELOW the assignment bar,
        // which carries an explicit z-1 and wins by DOM source order (bars render
        // after blocks). This keeps the bar hoverable/draggable on every overlap:
        // pending stripes, AND partial approved days (half/quarter/custom) where the
        // assignment is reduced + repositioned into the free zone rather than cut.
        // The block still catches hover wherever a bar doesn't physically sit.
        isPending
          ? 'z-0 time-off-pending border-dashed border-time-off-border-pending'
          : 'z-[1] time-off-approved border-time-off-border',
        { '-translate-y-1': isHovered }
      )}
      style={{
        left: `${startOffsetRem}rem`,
        width: `${widthRem}rem`,
        height: `${rect.heightPx}px`,
        top: `${rect.topPx}px`,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isSingleDay ? (
        <Info size={14} className="text-time-off-text" />
      ) : (
        <ScrollableText
          className={cn('w-full px-1.5 text-[10px] font-medium text-time-off-foreground-strong', {
            invisible: hasStickyLabel,
          })}
          animationDelayMs={TIME_OFF_TITLE_ANIMATION_DELAY_MS}
        >
          <span className="truncate">{label}</span>
        </ScrollableText>
      )}
    </div>
  );
});

TimeOffBlock.displayName = 'TimeOffBlock';
